import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import PropTypes from "prop-types";
import { styled } from "@mui/material/styles";
import {
  Container,
  Drawer as MuiDrawer,
  Toolbar,
  List,
  Box,
  ListItemButton,
  ListItemText,
  useMediaQuery,
  Link,
  Popover,
  Typography,
} from "@mui/material";
import Avatar from "@/components/avatar";
import PreLoader from "../preLoader";
import { drawerItems } from "./listItems";

import { useQuery } from "@apollo/client";
import { STORES, STOCK_TYPES } from "@/services/storeService";

import StoreActions from "@/store/store/actions";
import AuthActions from "@/store/auth/actions";
import { selectCurrentUser } from "@/store/auth/selectors";
import images from "@/assets/images";
import { Colors } from "@/theme";
import { MERCHANT_ID } from "@/constants/env";
import { PRICE_HISTORY_LIMIT } from "@/constants/config";

const StyledDrawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  "& .MuiDrawer-paper": {
    position: "relative",
    width: theme.spacing(15),
    [theme.breakpoints.up("sm")]: {
      width: theme.spacing(32),
    },
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    boxSizing: "border-box",
    boxShadow: "0px 3px 26px #00000017",
    ...(!open && {
      overflowX: "hidden",
      transition: theme.transitions.create("width", {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
      }),
      width: theme.spacing(15),
      [theme.breakpoints.up("sm")]: {
        width: theme.spacing(32),
      },
    }),
  },
}));

const AppLayout = (props) => {
  const { className, children } = props;
  const { t } = useTranslation();

  const dispatch = useDispatch();
  const user = useSelector(selectCurrentUser);
  const history = useHistory();
  const location = useLocation();
  const pathName = location.pathname;
  const isDesktop = useMediaQuery((theme) => theme.breakpoints.up("sm"));

  const [open, setOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const openPopper = Boolean(anchorEl);
  const popperId = openPopper ? "profile-popover" : undefined;

  const { loading: isLoadingStores } = useQuery(STORES, {
    variables: {
      limit: PRICE_HISTORY_LIMIT,
    },
    context: {
      headers: {
        merchantId: MERCHANT_ID,
      },
    },
    onCompleted: (data) => {
      dispatch(StoreActions.setStores(data.stores));
    },
    fetchPolicy: "no-cache",
  });

  const { loading: isLoadingStockTypes } = useQuery(STOCK_TYPES, {
    context: {
      headers: {
        merchantId: MERCHANT_ID,
      },
    },
    onCompleted: (data) => {
      dispatch(StoreActions.setStockTypes(data.stock_types));
    },
  });

  const onLogout = () => {
    dispatch(AuthActions.logout());
  };

  return (
    <div className={className}>
      <Box sx={{ display: "flex" }}>
        <StyledDrawer variant="permanent" open={open}>
          <Toolbar
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              px: [1],
              pt: 3,
            }}
          >
            <Link href="/main">
              <img
                src={images.logo}
                width={isDesktop ? 100 : 60}
                height={isDesktop ? 43 : 25}
              />
            </Link>
          </Toolbar>

          <List component="nav">
            {drawerItems(pathName).map((item) => (
              <ListItemButton
                selected={item.selected}
                key={`list-${item.id}`}
                onClick={() => history.push(item.redirectTo)}
                sx={{
                  py: 5,
                  flexDirection: "column",
                  boxShadow: item.selected ? "0px 3px 26px #0000001C" : "none",
                  "&.Mui-selected": {
                    backgroundColor: Colors.GREEN_128,
                  },
                  "&.Mui-selected:hover": {
                    backgroundColor: Colors.GREEN_128,
                  },
                  "&:hover": {
                    backgroundColor: Colors.GREEN_LIGHT,
                  },
                }}
              >
                {item.icon}
                {isDesktop && (
                  <ListItemText
                    disableTypography
                    sx={{
                      textAlign: "center",
                      color: item.selected
                        ? Colors.DIM_GREY
                        : Colors.GRAY_LIGHT,
                      fontWeight: item.selected ? 700 : 400,
                    }}
                  >
                    {t(`drawer.${item.id}`)}
                  </ListItemText>
                )}
              </ListItemButton>
            ))}
          </List>
          <Box
            display={"flex"}
            component={"main"}
            pb={5}
            sx={{
              flex: "auto",
              alignItems: "flex-end",
              justifyContent: "center",
            }}
          >
            <Avatar
              size={isDesktop ? 65 : 40}
              url={user?.profile?.thumbnail}
              firstLetter={user?.first_name?.[0]}
              lastLetter={user?.last_name?.[0]}
              onClick={(e) => setAnchorEl(e.currentTarget)}
            />
          </Box>
        </StyledDrawer>
        <Box
          alignItems={"center"}
          component="main"
          sx={{
            backgroundColor: Colors.MAIN_BG,
            flexGrow: 1,
            height: "100vh",
            overflow: "auto",
          }}
        >
          <Container
            maxWidth={false}
            sx={{
              paddingX: { xs: 3, md: 10 },
              my: 15,
            }}
          >
            {children}
          </Container>
        </Box>
      </Box>
      {(isLoadingStores || isLoadingStockTypes) && <PreLoader size={40} />}

      <Popover
        id={popperId}
        open={openPopper}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{
          vertical: "center",
          horizontal: "right",
        }}
      >
        <div onClick={onLogout}>
          <Typography sx={{ p: 4, cursor: "pointer" }}>
            {t("drawer.logout")}
          </Typography>
        </div>
      </Popover>
    </div>
  );
};

AppLayout.propTypes = {
  className: PropTypes.string,
};

export default AppLayout;
